import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { APP_TITLE_SUFFIX, LOGO_URL } from '@/constants/appMeta';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Lock, User, FileCheck, Gauge, ShieldCheck } from 'lucide-react';

export default function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  useEffect(() => {
    document.title = `${t('login.submit')} | ${APP_TITLE_SUFFIX}`;
  }, [t]);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('login.error-invalid'));
    } finally {
      setLoading(false);
    }
  };

  const year = new Date().getFullYear();
  const features = [
    { Icon: FileCheck, text: t('login.feature-certificates') },
    { Icon: Gauge, text: t('login.feature-devices') },
    { Icon: ShieldCheck, text: t('login.feature-secure') },
  ];

  return (
    <div className="min-h-screen w-full flex" style={{ background: '#f8fafc' }}>
      {/* Levý brandový panel (desktop) */}
      <aside
        className="relative hidden lg:flex flex-col justify-between overflow-hidden"
        style={{
          width: '44%',
          maxWidth: '34rem',
          padding: '3rem',
          color: '#e2e8f0',
          background: 'linear-gradient(160deg, #0f2a6b 0%, #1d4ed8 55%, #2563eb 100%)',
        }}
      >
        <div
          className="absolute rounded-full pointer-events-none"
          style={{ top: '-6rem', right: '-6rem', width: '22rem', height: '22rem', filter: 'blur(60px)', background: 'rgba(96, 165, 250, 0.35)' }}
        />
        <div
          className="absolute rounded-full pointer-events-none"
          style={{ bottom: '-8rem', left: '-4rem', width: '20rem', height: '20rem', filter: 'blur(60px)', background: 'rgba(37, 99, 235, 0.45)' }}
        />

        <div className="relative z-10 flex items-center gap-3">
          <img src={LOGO_URL} alt="CalibraTech" className="w-10 h-10 rounded-lg object-contain p-1" style={{ background: '#ffffff' }} />
          <span className="text-xl font-bold tracking-tight text-white" style={{ fontFamily: 'Inter, sans-serif' }}>
            CalibraTech&nbsp;2.0
          </span>
        </div>

        <div className="relative z-10">
          <h2 className="text-white font-bold m-0 mb-4" style={{ fontFamily: 'Inter, sans-serif', fontSize: '2rem', lineHeight: 1.2, letterSpacing: '-0.02em' }}>
            {t('login.headline')}
          </h2>
          <p className="m-0 mb-8" style={{ fontSize: '1rem', lineHeight: 1.6, color: 'rgba(226, 232, 240, 0.85)', maxWidth: '24rem' }}>
            {t('login.lead')}
          </p>

          <ul className="list-none p-0 m-0 flex flex-col gap-4">
            {features.map(({ Icon, text }, i) => (
              <li key={i} className="flex items-center gap-3 text-white" style={{ fontSize: '0.95rem' }}>
                <span className="flex-none" style={{ color: '#bfdbfe', background: 'rgba(255, 255, 255, 0.12)', borderRadius: '0.5rem', padding: '0.4rem' }}>
                  <Icon className="w-5 h-5" />
                </span>
                <span>{text}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative z-10 m-0" style={{ fontSize: '0.75rem', color: 'rgba(226, 232, 240, 0.7)' }}>
          CalibraTech 2.0 &copy; {year}
        </p>
      </aside>

      {/* Pravá strana — přihlašovací formulář */}
      <main className="flex-1 flex items-center justify-center" style={{ padding: '2rem 1.5rem' }}>
        <div className="w-full" style={{ maxWidth: '26rem' }}>
          {/* Kompaktní hlavička jen pro mobil */}
          <div className="flex lg:hidden items-center justify-center gap-2.5 mb-8">
            <img src={LOGO_URL} alt="CalibraTech" className="w-9 h-9 rounded-lg object-contain" />
            <span className="text-lg font-bold" style={{ fontFamily: 'Inter, sans-serif', color: '#2563eb' }}>
              CalibraTech 2.0
            </span>
          </div>

          <div
            style={{
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              borderRadius: '0.75rem',
              padding: '2rem',
              boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.08), 0 4px 10px -6px rgba(15, 23, 42, 0.05)',
            }}
          >
            <div className="mb-6">
              <h1 className="m-0 font-bold" style={{ fontFamily: 'Inter, sans-serif', fontSize: '1.625rem', letterSpacing: '-0.02em', color: '#0f172a' }}>
                {t('login.submit')}
              </h1>
              <p className="m-0 mt-2" style={{ fontSize: '0.9rem', color: '#475569' }}>
                {t('login.subtitle')}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium uppercase tracking-wider" style={{ color: '#475569' }}>
                  {t('login.username')}
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#94a3b8' }} />
                  <Input
                    data-testid="login-username-input"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder={t('login.username-placeholder')}
                    className="pl-10 h-10"
                    autoFocus
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium uppercase tracking-wider" style={{ color: '#475569' }}>
                  {t('login.password')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#94a3b8' }} />
                  <Input
                    data-testid="login-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('login.password-placeholder')}
                    className="pl-10 h-10"
                  />
                </div>
              </div>

              {error && (
                <div data-testid="login-error" className="text-sm p-2 rounded" style={{ background: 'var(--ct-error-soft)', color: 'var(--ct-error)', border: '1px solid var(--ct-error)' }}>
                  {error}
                </div>
              )}

              <Button
                data-testid="login-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full font-semibold"
                style={{ background: '#2563eb', color: '#fff', height: '2.75rem', borderRadius: '0.5rem' }}
              >
                {loading ? t('login.loading') : t('login.submit')}
              </Button>
            </form>
          </div>

          <p className="text-center mt-5" style={{ fontSize: '0.85rem', color: '#475569' }}>
            Nemáte účet?{' '}
            <Link to="/register-lab" className="font-medium" style={{ color: '#2563eb' }}>
              Zaregistrujte svou laboratoř
            </Link>
          </p>

          <p className="text-center mt-4" style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
            CalibraTech 2.0 &copy; {year}
          </p>
        </div>
      </main>
    </div>
  );
}
